import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyIcon from "@mui/icons-material/Key";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CopyIcon from "@mui/icons-material/ContentCopy";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  FormLabel,
  FormControl,
  FormHelperText,
  Stack,
  Tooltip,
  Grid,
} from "@mui/material";
import React, { useState } from "react";
import {
  useAddApiKeyIdentity,
  useApiKeyIdentities,
  useRemoveApiKeyIdentity,
} from "../hooks/useApiIdentities";
import { useZudokuTheme } from "../hooks/useZudokuTheme";
import { CreateApiKeyInput } from "../services/apiIdentityService";
import {
  getMutedTextStyle,
  getTableDataCellStyle,
} from "../styling/fieldStyles";

interface ApiKeyIdentitiesProps {
  onRefresh: () => void;
}

interface AddApiKeyDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (apiKey: CreateApiKeyInput & { id: string }) => Promise<void>;
}

function AddApiKeyDialog({ open, onClose, onAdd }: AddApiKeyDialogProps) {
  const [label, setLabel] = useState("");
  const [key, setKey] = useState("");
  const [userName, setUserName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim() || !key.trim()) {
      setError("Label and Key are required");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onAdd({
        id: `api-key-${Date.now()}`, // Simple ID generation
        label: label.trim(),
        key: key.trim(),
        userName: userName.trim() || undefined,
      });
      setLabel("");
      setKey("");
      setUserName("");
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add API key");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setLabel("");
      setKey("");
      setUserName("");
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add API Key Identity</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={3}>
            <FormControl required>
              <FormLabel>Label</FormLabel>
              <TextField
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. My API Key"
                disabled={isSubmitting}
                required
              />
            </FormControl>

            <FormControl required>
              <FormLabel>API Key</FormLabel>
              <TextField
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="Enter your API key"
                disabled={isSubmitting}
                required
              />
            </FormControl>

            <FormControl>
              <FormLabel>Username (Optional)</FormLabel>
              <TextField
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="For Basic Auth (Personal Access Token)"
                disabled={isSubmitting}
              />
              <FormHelperText>
                If you provide a username, this will use Basic Authentication.
                Otherwise, it will use Token authentication.
              </FormHelperText>
            </FormControl>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || !label.trim() || !key.trim()}
          variant="contained"
        >
          {isSubmitting ? "Adding..." : "Add API Key"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ApiKeyIdentities({ onRefresh }: ApiKeyIdentitiesProps) {
  const { currentTheme } = useZudokuTheme();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
  const { data: apiKeys, isLoading, isError } = useApiKeyIdentities();
  const { mutateAsync: addApiKey } = useAddApiKeyIdentity();
  const { mutateAsync: removeApiKey } = useRemoveApiKeyIdentity();

  const mutedTextStyle = getMutedTextStyle(currentTheme);
  const tableDataStyle = getTableDataCellStyle(currentTheme);

  if (isLoading) {
    return <CircularProgress />;
  }

  if (isError || !apiKeys) {
    return <Alert severity="error">Failed to load API keys</Alert>;
  }

  const handleAddApiKey = async (
    apiKeyData: CreateApiKeyInput & { id: string }
  ) => {
    await addApiKey(apiKeyData);
    onRefresh();
  };

  const handleDeleteApiKey = async (apiKeyId: string) => {
    setIsDeleting(apiKeyId);
    try {
      await removeApiKey(apiKeyId);
      onRefresh();
    } catch (err) {
      console.error("Failed to delete API key:", err);
    } finally {
      setIsDeleting(null);
    }
  };

  const toggleKeyVisibility = (apiKeyId: string) => {
    const newVisibleKeys = new Set(visibleKeys);
    if (newVisibleKeys.has(apiKeyId)) {
      newVisibleKeys.delete(apiKeyId);
    } else {
      newVisibleKeys.add(apiKeyId);
    }
    setVisibleKeys(newVisibleKeys);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const maskKey = (key: string) => {
    if (key.length <= 8) return "••••••••";
    return key.slice(0, 4) + "••••••••" + key.slice(-4);
  };

  return (
    <Box>
      <Grid container spacing={2} alignItems="center" justifyContent="space-between" mb={3}>
        <Grid size={{ xs: 12, sm: "auto" }}>
          <Typography
            variant="h3"
            sx={{
              mb: 1,
            }}
          >
            API Key Identities
          </Typography>
          <Typography variant="body2" sx={mutedTextStyle}>
            Manage API keys for authentication
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: "auto" }}>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            variant="contained"
            fullWidth
          >
            Add API Key
          </Button>
        </Grid>
      </Grid>

      {apiKeys.length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            backgroundColor: currentTheme.accent,
            border: `1px solid ${currentTheme.border}`,
            borderRadius: 1,
          }}
        >
          <KeyIcon
            sx={{ fontSize: 48, color: currentTheme.mutedForeground, mb: 2 }}
          />
          <Typography
            variant="h4"
            sx={{
              ...mutedTextStyle,
              mb: 1,
            }}
          >
            No API Key Identities
          </Typography>
          <Typography variant="body2" sx={mutedTextStyle}>
            Add your first API key to get started with API authentication
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 1,
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>API Key</TableCell>
                <TableCell>Authentication</TableCell>
                <TableCell
                  sx={{
                    width: 60,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {apiKeys.map((apiKey) => {
                const isVisible = visibleKeys.has(apiKey.id);
                const displayKey = isVisible ? apiKey.key : maskKey(apiKey.key);

                return (
                  <TableRow key={apiKey.id}>
                    <TableCell sx={tableDataStyle}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <KeyIcon sx={{ color: currentTheme.mutedForeground }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1">
                            {apiKey.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              ...mutedTextStyle,
                              fontFamily: "monospace",
                              fontSize: "0.75rem",
                            }}
                          >
                            {displayKey}
                          </Typography>
                        </Box>
                        <Tooltip title="Show">
                          <IconButton
                            size="small"
                            onClick={() => toggleKeyVisibility(apiKey.id)}
                            sx={{ color: currentTheme.mutedForeground }}
                          >
                            {isVisible ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy">
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(apiKey.key)}
                            sx={{ color: currentTheme.mutedForeground }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={tableDataStyle}>
                      <Chip
                        label={apiKey.userName ? "Basic Auth" : "Token Auth"}
                        size="small"
                        color={apiKey.userName ? "primary" : "secondary"}
                        variant="outlined"
                        sx={{
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={tableDataStyle}>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteApiKey(apiKey.id)}
                          disabled={isDeleting === apiKey.id}
                          sx={{
                            color: currentTheme.mutedForeground,
                            "&:hover": {
                              color: "error.main",
                            },
                          }}
                        >
                          {isDeleting === apiKey.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AddApiKeyDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleAddApiKey}
      />
    </Box>
  );
}
